import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { frozenRateFields } from './fx.js';
import { pagedQuery, pagedResponse } from './pagination.js';
import { CurrencySchema, IsoDateSchema, MessageSchema, MoneyStringSchema, } from './primitives.js';
import { SETTINGS_REGISTRY } from './org-settings.js';
import { PaymentTenderSchema, PaymentTendersInputSchema, ReceiptTenderMethodSchema, TENDER_MESSAGES, tenderSummaryFields, } from './tenders.js';
const c = initContract();
/**
 * Payment Vouchers (PART 11, FIN-04; purchasing entry points at PUR-05) —
 * money out to a supplier, spread across the bills it settles, or a direct
 * expense to a named account. The receipt voucher's mirror, plus the
 * approval axis.
 *
 * Permission area: `accounting` — FIN-04 §9: "Reaching this screen requires
 * the accounting-area permission", and 16.2.2 row 6 lists vouchers under
 * `accounting`. Per action, exactly as FIN-03's template: read =
 * accounting.view · create/edit a draft, allocate, cancel a draft =
 * accounting.create/edit · delete a draft = accounting.delete · post AND
 * approve = accounting.approve (the §9 table's "payment-approve" and
 * "payment-voucher post" permissions both resolve to the area's Approve
 * action — the twelve-area grid has no finer switch). A purchasing user
 * without the area reaches the screen only from a bill's Record payment
 * button (PUR-05), which carries the supplier and bill across (Q-FIN-04-6's
 * proposed default, adopted).
 *
 * Allocation rules: identical to FIN-03 reading "supplier" for "customer"
 * and "bill" for "invoice" (FIN-04 §10) — both over-allocation tests within
 * half a cent, oldest-first auto-allocate with the zero-amount write-back,
 * the excess held as SUPPLIER CREDIT (BR-FIN-04-08), R9 throughout.
 *
 * The approval axis (§5a): require_approval is off by default and FORCED ON
 * above the company's `treasury.payment_approval_limit` setting (Q-FIN-04-1's
 * 25,000 is the shipped default); the
 * gate is `off OR approved` (BR-FIN-04-01/-02); Approved stamps the approver
 * and date, anything else clears them (BR-FIN-04-03/-04); the approver must
 * not be the creator (Q-FIN-04-2, §9); any change to amount, supplier or
 * allocation resets the approval to Pending (the listed prototype defect,
 * fixed). Approval history rides the audit log (Q-FIN-04-3).
 *
 * Lifecycle per R8 / BR-FIN-04-16: drafts freely editable and deletable
 * whatever the approval status; Draft → Posted and Draft → Cancelled are the
 * ONLY transitions; a posted voucher is corrected by the `reverse` route
 * (BR-FIN-04-11): the opposite entry reopens every bill it paid, each by
 * the amount that had been applied to it, and the original stays posted and
 * visible. Reason required and audited; refused while the voucher's bank
 * line sits in a completed reconciliation (BR-FIN-04-12) or a cheque record
 * rides it. Dated the company's TODAY (GEN-73); one reversal ever
 * (FIN-02 §15). The approval axis does NOT re-enter: approval gates money
 * LEAVING (BR-FIN-04-02); a reversal brings it back.
 */
/**
 * Q-FIN-04-1's threshold. Since PART 15 landed this is the COMPANY SETTING
 * `treasury.payment_approval_limit`; this constant is its registry default,
 * kept for the UI's optimistic fallback. The forced-approval gate reads the
 * setting per request.
 */
export const PAYMENT_APPROVAL_LIMIT = SETTINGS_REGISTRY['treasury.payment_approval_limit']
    .default;
// ── The fixed sentences of FIN-04 ───────────────────────────────────────────
export const PAYMENT_MESSAGES = {
    /**
     * BR-FIN-04-02, quoted exactly — the amber line under the Post button, and
     * the sentence for both ERR-FIN-04-01 (still pending) and ERR-FIN-04-02
     * (rejected, then posted anyway).
     */
    approvalRequired: 'Approval required — this payment must be approved before it can be posted.',
    /** Q-FIN-04-2 / FIN-04 §9. */
    selfApproval: 'A payment must not be approved by the person who created it.',
    /** FIN-04 §7's posted notice, quoted exactly. */
    postedLocked: 'This payment is posted and locked. A journal entry was generated and the supplier balance updated.',
    cancelledLocked: 'This payment is cancelled and read-only.',
    /** GEN-59: the cancelling act carries its reason. */
    cancelReasonRequired: 'A payment can only be cancelled with a reason, which is stored on the voucher and written to the audit log.',
    amountRequired: 'Enter an amount paid above zero before posting.',
    supplierRequired: 'A supplier payment must name a supplier.',
    /** ERR-FIN-04-04's field. */
    debitAccountRequired: 'Other expense must name an account to debit.',
    /** BR-FIN-03-04 read across: "Paid from" is a cash-family account. */
    cashAccountsOnly: 'Paid from must be one of the accounts inside Cash & Cash Equivalents.',
    /** BR-FIN-04-05: only expense accounts are offered. */
    debitAccountKind: 'An expense payment can only debit an expense account.',
    /**
     * Allocation targets: posted bills of THIS supplier. A bill in another
     * currency is refused with `FX_REFUSALS.settleCurrencyMismatch`.
     */
    allocationBillInvalid: 'Money can only be applied to posted bills of this supplier.',
    overAllocated: 'Over-allocated — amount applied exceeds paid or bill balance',
    /** BR-FIN-04-11 — no route out of Posted. */
    noRouteOutOfPosted: 'A posted payment is corrected by a reversing entry, never rewritten and never cancelled.',
    /** FIN-04 §15's empty state. */
    noOpenBills: 'No open bills for this supplier.',
    /** A locked voucher's approval controls are inert (BR-FIN-04-07). */
    approvalLocked: 'This voucher is locked. The approval controls are inert.',
    /** BR-FIN-02-10 read across: only the posted state reverses. */
    reverseOnlyPosted: 'Only a posted payment can be reversed. A draft is edited or deleted instead.',
    /** One reversal per voucher — FIN-02 §15's "no reversing a reversal" kept. */
    alreadyReversed: 'This payment has already been reversed. Both entries remain in the books.',
    /** The GEN-72 shape applied to the reversing act. */
    reverseReasonRequired: 'A posted payment can only be reversed with a reason, which is written to the audit log.',
    /** Q-FIN-04-5's register record rides the voucher — GEN-74's principle, blocker named. */
    chequeRides: (chequeCode) => `Cheque ${chequeCode} was recorded from this payment. Correct it in the cheque register — reversing a bounced cheque undoes this voucher with it.`,
    /** BR-FIN-04-12, the spec's own sentence with the statement date live. */
    reconciledLocked: (statementDate) => `This payment is reconciled on the ${statementDate} statement. Reopen the reconciliation before reversing it.`,
    /** PUR-04 §5 field 8: only THIS supplier's posted debit notes carry credit. */
    allocationNoteInvalid: "Supplier credit can only be applied from this supplier's posted debit notes.",
    /** The per-note ceiling: grand total less what posted vouchers already applied. */
    noteOverApplied: (noteCode, left) => `${noteCode} has ${left} of credit left to apply.`,
};
/**
 * D46 — FIN-04's three parameterised payment refusals, structured.
 * `chequeRides` carries a cheque code, `reconciledLocked` a statement date,
 * `noteOverApplied` a debit-note code and a PRE-FORMATTED money string (`left`,
 * interpolated verbatim — nothing pluralises on it). Sentence parity pinned by
 * `refusal-census.spec.ts`.
 */
export const PAYMENT_REFUSALS = {
    chequeRides: (chequeCode) => ({
        message: PAYMENT_MESSAGES.chequeRides(chequeCode),
        key: 'pay.errChequeRides',
        params: { chequeCode },
    }),
    reconciledLocked: (statementDate) => ({
        message: PAYMENT_MESSAGES.reconciledLocked(statementDate),
        key: 'pay.errReconciledLocked',
        params: { statementDate },
    }),
    noteOverApplied: (noteCode, left) => ({
        message: PAYMENT_MESSAGES.noteOverApplied(noteCode, left),
        key: 'pay.errNoteOverApplied',
        params: { noteCode, left },
    }),
};
// ── Shapes ──────────────────────────────────────────────────────────────────
export const PaymentStatusSchema = z.enum(['draft', 'posted', 'cancelled']);
export const PaymentTypeSchema = z.enum(['supplier', 'expense']);
export const ApprovalStatusSchema = z.enum(['pending', 'approved', 'rejected']);
/**
 * The voucher HEAD's method — a derived summary since the tenders wave (S6):
 * the one tender's method (`endorsed_check` included, PV-01), or `mixed`
 * when the document carries more than one. Never sent on a write.
 */
export const PaymentMethodOutSchema = z.enum([
    'cash',
    'card',
    'bank_transfer',
    'check',
    'online',
    'endorsed_check',
    'mixed',
]);
/** One row of the allocation grid: five derived facts + the editable cell. */
export const PaymentAllocationRowSchema = z
    .object({
    billId: z.uuid(),
    billCode: z.string(),
    billDate: IsoDateSchema,
    billTotal: z.string(),
    /** POSTED payments only (R9). Excludes this voucher while it is a draft. */
    alreadyPaid: z.string(),
    balanceDue: z.string(),
    amount: z.string(),
    /**
     * PUR-04 §5 field 8's other half: set, this row applies the named debit
     * note's standing credit instead of the voucher's cash ("Apply to
     * supplier credit … where the credit is set against what is owed").
     * Consumes none of `amount`'s cash budget; capped per note at its
     * grand total less what POSTED vouchers already applied.
     */
    purchaseReturnId: z.uuid().nullable(),
    purchaseReturnCode: z.string().nullable(),
})
    .strict();
/** An open bill offered by the picker (posted, balance above tolerance). */
export const OpenBillSchema = z
    .object({
    billId: z.uuid(),
    billCode: z.string(),
    billDate: IsoDateSchema,
    billTotal: z.string(),
    alreadyPaid: z.string(),
    balanceDue: z.string(),
})
    .strict();
/**
 * BR-FIN-03-10 (read across per F-89) for a voucher that does not exist yet —
 * the supplier-side twin of `receipts.ts`'s preview, and the same argument:
 * the browser folded the supplier's WHOLE open set to answer this, which is
 * what kept `GET /api/payments/open-bills` unpaged.
 *
 * The walk spreads CASH only. A note-funded row (PUR-04 §5 field 8) spends a
 * debit note's standing credit rather than the voucher's money, so it is not
 * something an amount can be spread across — the saved-voucher route has the
 * same shape, and the screen re-attaches its credit rows on top of the answer.
 */
export const PaymentAutoAllocatePreviewRequestSchema = z
    .object({
    partnerId: z.uuid(),
    /** The voucher's currency — absent = the supplier's default. */
    currency: CurrencySchema.optional(),
    /** Absent IS the zero-amount shortcut: fill every bill, write the total back. */
    amount: MoneyStringSchema.optional(),
})
    .strict();
export const PaymentAutoAllocatePreviewSchema = z
    .object({
    /** Only the bills that receive money, oldest first. */
    allocations: z.object({ billId: z.uuid(), amount: MoneyStringSchema }).strict().array(),
    totalAllocated: MoneyStringSchema,
    /** The amount box after the walk; the written-back total on the shortcut. */
    amountReceived: MoneyStringSchema,
    /** What the walk could not place — a standing advance on the supplier. */
    unallocated: MoneyStringSchema,
})
    .strict();
export const PaymentSchema = z
    .object({
    id: z.uuid(),
    /** PV-#### (FIN-04 field 1). */
    code: z.string(),
    paymentNo: z.number().int().positive(),
    status: PaymentStatusSchema,
    /** GEN-59: why the draft was voided — null until cancelled, and on rows cancelled before the reason existed. */
    cancelReason: z.string().nullable(),
    paymentDate: IsoDateSchema,
    paymentType: PaymentTypeSchema,
    partnerId: z.uuid().nullable(),
    partnerCode: z.string().nullable(),
    partnerName: z.string().nullable(),
    /** The cash account credited on posting ("Paid from"). */
    paidFromAccountId: z.uuid(),
    paidFromAccountCode: z.string(),
    method: PaymentMethodOutSchema,
    reference: z.string().nullable(),
    /**
     * BR-FIN-04-14 — the date on the CHEQUE, which is not the date on the
     * voucher. Always present: for anything not paid by cheque, and for a
     * cheque dated the day it was written, it is `paymentDate` itself. Later
     * than `paymentDate` means the bank leg is held until this date (D31).
     */
    chequeDate: IsoDateSchema,
    /** The total in the VOUCHER's currency — Σ tenders, each converted to it. */
    amount: z.string(),
    currency: z.string(),
    /** EC-19-10: the rate this document froze when it was confirmed, and its "as of" date. */
    ...frozenRateFields,
    /** S6 — one row per way the money went out (PV-01 adds the endorsed cheque). */
    tenders: PaymentTenderSchema.array(),
    ...tenderSummaryFields,
    expenseCategory: z.string().nullable(),
    expenseDescription: z.string().nullable(),
    debitAccountId: z.uuid().nullable(),
    /** The approval axis (FIN-04 §5a). */
    requireApproval: z.boolean(),
    approvalStatus: ApprovalStatusSchema,
    approvedBy: z.uuid().nullable(),
    approvedByEmail: z.string().nullable(),
    approvalDate: IsoDateSchema.nullable(),
    allocations: PaymentAllocationRowSchema.array(),
    totalApplied: z.string(),
    /** BR-FIN-04-08 — the excess held as supplier credit, never a block. */
    unallocated: z.string(),
    notes: z.string().nullable(),
    journalEntryId: z.uuid().nullable(),
    postedAt: z.string().nullable(),
    /**
     * The reversal stamp (BR-FIN-04-11, R8). Set by the reverse route and by
     * a cheque bounce (FIN-11 §6). While set, the voucher's allocations no
     * longer count toward any "already paid" — every bill reopens by the
     * amount applied — and the voucher stays posted, locked and visible.
     */
    reversedAt: z.string().nullable(),
    /** The opposite entry — one hop each way: this link here, `reversalOf` on the entry. */
    reversalJournalEntryId: z.uuid().nullable(),
    /** The entry's display code (JE-…), so the screen can name it without a second fetch. */
    reversalEntryCode: z.string().nullable(),
    /** Required by the reverse route; NULL on a bounce-stamped voucher. */
    reversalReason: z.string().nullable(),
    createdAt: z.string(),
})
    .strict();
const PaymentAllocationInputSchema = z
    .object({
    billId: z.uuid(),
    amount: MoneyStringSchema,
    /** Set = this row is funded by the debit note's credit, not by cash. */
    purchaseReturnId: z.uuid().optional(),
})
    .strict();
/**
 * TWO SHAPES, ONE MEANING — see `CreateReceiptRequestSchema`: `tenders` (S6),
 * or the pre-wave `method + paidFromAccountId (+ amount, chequeDate)`, which
 * the server folds into one tender (the cheque date becoming that tender's
 * due date). Never both.
 */
export const CreatePaymentRequestSchema = z
    .object({
    paymentDate: IsoDateSchema,
    paymentType: PaymentTypeSchema.default('supplier'),
    partnerId: z.uuid().optional(),
    /**
     * The voucher's currency (owner decision, 30 Oct 2026) — absent = the supplier's
     * default currency. A voucher settles only bills in its OWN currency.
     */
    currency: CurrencySchema.optional(),
    paidFromAccountId: z.uuid().optional(),
    method: ReceiptTenderMethodSchema.optional(),
    /** S6 — the tender lines. */
    tenders: PaymentTendersInputSchema.optional(),
    reference: z.string().trim().max(60).optional(),
    /**
     * BR-FIN-04-14, and ABSENT IS THE IDENTITY: no cheque date given means the
     * cheque is dated the day the voucher is, which is what every payment in
     * the build did before the field existed. Only a `check`-method payment
     * can separate the two; anything else is normalised back to `paymentDate`
     * by the service, with `payments_cheque_date_shape` as the floor.
     */
    chequeDate: IsoDateSchema.optional(),
    amount: MoneyStringSchema.optional(),
    expenseCategory: z.string().trim().max(60).optional(),
    expenseDescription: z.string().trim().max(200).optional(),
    debitAccountId: z.uuid().optional(),
    requireApproval: z.boolean().optional(),
    allocations: PaymentAllocationInputSchema.array().optional(),
    notes: z.string().max(2000).optional(),
})
    .strict()
    .superRefine((body, ctx) => {
    if (body.tenders === undefined) {
        if (body.method === undefined) {
            ctx.addIssue({ code: 'custom', path: ['tenders'], message: TENDER_MESSAGES.atLeastOne });
        }
        if (body.paidFromAccountId === undefined) {
            ctx.addIssue({
                code: 'custom',
                path: ['paidFromAccountId'],
                message: TENDER_MESSAGES.accountRequired,
            });
        }
    }
    else if (body.method !== undefined ||
        body.amount !== undefined ||
        body.chequeDate !== undefined) {
        ctx.addIssue({ code: 'custom', path: ['tenders'], message: TENDER_MESSAGES.notBoth });
    }
});
export const UpdatePaymentRequestSchema = z
    .object({
    paymentDate: IsoDateSchema.optional(),
    /** Changing the supplier clears the whole allocation (BR-FIN-03-03 read across). */
    partnerId: z.uuid().optional(),
    /** See `UpdateReceiptRequestSchema.currency` — a change clears the allocation. */
    currency: CurrencySchema.optional(),
    paidFromAccountId: z.uuid().optional(),
    method: ReceiptTenderMethodSchema.optional(),
    /** S6 — REPLACES the tender set; see `UpdateReceiptRequestSchema.tenders`. */
    tenders: PaymentTendersInputSchema.optional(),
    reference: z.string().trim().max(60).nullable().optional(),
    /**
     * Absent means "leave it as it is" for a cheque date somebody has already
     * separated from the payment date, and "follow the payment date" for one
     * that has never been separated — the same rule D31 gave `posting_due_on`,
     * which moves with the cheque date it was taken from. Switching the method
     * away from Check gives the identity back.
     *
     * NULL is how the screen says "clear it": back to the payment's own date.
     * The distinction is the reason this is nullable rather than merely
     * optional — absent and null would otherwise be the same request, and
     * emptying the box would silently leave yesterday's future date in place.
     */
    chequeDate: IsoDateSchema.nullable().optional(),
    amount: MoneyStringSchema.optional(),
    expenseCategory: z.string().trim().max(60).nullable().optional(),
    expenseDescription: z.string().trim().max(200).nullable().optional(),
    debitAccountId: z.uuid().nullable().optional(),
    requireApproval: z.boolean().optional(),
    notes: z.string().max(2000).nullable().optional(),
})
    .strict();
export const SetPaymentAllocationsRequestSchema = z
    .object({
    allocations: PaymentAllocationInputSchema.array(),
})
    .strict();
export const SetApprovalRequestSchema = z
    .object({
    /** Approved stamps the caller + today; Pending/Rejected clear both (BR-FIN-04-03). */
    status: ApprovalStatusSchema,
})
    .strict();
export const ReversePaymentRequestSchema = z
    .object({
    /** Required and audited — the GEN-72 shape for a correction to posted books. */
    reason: z.string().trim().min(1, PAYMENT_MESSAGES.reverseReasonRequired).max(500),
})
    .strict();
const paymentParams = z.object({ paymentId: z.uuid() }).strict();
export const paymentsContract = c.router({
    list: {
        method: 'GET',
        path: '/api/payments',
        query: pagedQuery({
            partnerId: z.uuid().optional(),
            status: PaymentStatusSchema.optional(),
            /** RV-01's payment twin — voucher number, payee name, reference/cheque number. */
            q: z.string().trim().max(120).optional(),
        }),
        responses: {
            200: pagedResponse('payments', PaymentSchema),
            401: MessageSchema,
            403: MessageSchema,
        },
        summary: 'Payment vouchers (accounting.view), PAGED (NFR-42) — both filters in SQL.',
    },
    get: {
        method: 'GET',
        path: '/api/payments/:paymentId',
        pathParams: paymentParams,
        responses: {
            200: PaymentSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
        },
        summary: 'One voucher with its allocation grid and approval panel (accounting.view).',
    },
    /**
     * PAGED (NFR-42) — the supplier-side twin of `receipts.openInvoices`, and
     * the same two-wave story: the fold moved to `autoAllocatePreview` below,
     * then `allocationBody()` stopped rebuilding the SAVE payload from the
     * visible rows (PAGINATION.md §3 step 13), and only then could a LIMIT go
     * in front of this read. The rows keep their `bills` key; the order is
     * oldest first with the bill number as the tiebreak.
     *
     * `q` is GEN-50's half, added by the wave that converted the LAST picker
     * on this screen — the credit grid's "which bill does this note pay?"
     * field, which was a `<select>` over the bills the grid had DRAWN. That
     * `<select>` could not offer a bill the reader had not paged to, so the
     * search has to narrow the SET and let the LIMIT cut the page out of the
     * result — never the other way round, which is a filter over one page
     * wearing a search box's clothes.
     *
     * IT MATCHES THE PRINTED NUMBER ONLY, and that is the difference from the
     * partner precedent's `q` rather than an omission: `documentLabelSearch`
     * exists because a document picker prints `CODE — Partner`, and this
     * route is ALREADY scoped to one `partnerId`, so the partner arm could
     * only ever match all of the rows or none of them. What the reader sees
     * on the row is the code, and the code is what the box matches — through
     * `app_format_number`, so BR-MD-13-07 keeps deciding what a bill numbered
     * under an old format looks like.
     *
     * THERE IS DELIBERATELY NO `ids` HERE. The paging wave considered one for
     * Q-FIN-04-6's carry and declined — "a wider change for a narrower gain"
     * — because the screen already resolves the bills it HOLDS by other
     * means: `useBill` for the carried id, and the voucher's own allocation
     * rows, which arrive carrying their `billCode`. The typeahead's
     * `selected` prop is fed from those, so the conversion adds a search
     * without adding a second resolve read.
     */
    openBills: {
        method: 'GET',
        path: '/api/payments/open-bills',
        query: pagedQuery({
            partnerId: z.uuid(),
            /** The voucher's currency — absent = the supplier's default. */
            currency: CurrencySchema.optional(),
            /** GEN-50's search box: matches the bill's PRINTED number. */
            q: z.string().optional(),
        }),
        responses: {
            200: pagedResponse('bills', OpenBillSchema),
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
        },
        summary: "The supplier's open POSTED bills for the allocation grid (accounting.view), oldest first, one page at a time — drafts and on-hold bills never appear (R9, BR-FIN-04-13).",
    },
    create: {
        method: 'POST',
        path: '/api/payments',
        body: CreatePaymentRequestSchema,
        responses: {
            201: PaymentSchema,
            400: MessageSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'New draft voucher (accounting.create). Currency adopts the supplier; above the payment-approval-limit setting the approval switch is forced ON (Q-FIN-04-1).',
    },
    update: {
        method: 'PATCH',
        path: '/api/payments/:paymentId',
        pathParams: paymentParams,
        body: UpdatePaymentRequestSchema,
        responses: {
            200: PaymentSchema,
            400: MessageSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'Edit a DRAFT (accounting.edit). Changing amount/supplier resets the approval to Pending (the FIN-04 defect list, fixed); changing the supplier clears the allocation; posted/cancelled answer 409.',
    },
    setAllocations: {
        method: 'PUT',
        path: '/api/payments/:paymentId/allocations',
        pathParams: paymentParams,
        body: SetPaymentAllocationsRequestSchema,
        responses: {
            200: PaymentSchema,
            400: MessageSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'Replace the allocation grid (accounting.edit). Refused when over-allocated by either test, half-cent tolerance. Resets the approval to Pending.',
    },
    /** The same walk, for a voucher that has not been saved. Persists nothing. */
    autoAllocatePreview: {
        method: 'POST',
        path: '/api/payments/auto-allocate-preview',
        body: PaymentAutoAllocatePreviewRequestSchema,
        responses: {
            200: PaymentAutoAllocatePreviewSchema,
            400: MessageSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
        },
        summary: "Oldest-first auto-allocate for an UNSAVED voucher (accounting.view, BR-FIN-03-10) — computed over the supplier's WHOLE open set and persisted nowhere.",
    },
    autoAllocate: {
        method: 'POST',
        path: '/api/payments/:paymentId/auto-allocate',
        pathParams: paymentParams,
        body: z.object({}).strict().optional(),
        responses: {
            200: PaymentSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'Auto-allocate oldest-first (accounting.edit, BR-FIN-03-10 read across); with a zero amount, fills every bill and writes the total back.',
    },
    setApproval: {
        method: 'POST',
        path: '/api/payments/:paymentId/approval',
        pathParams: paymentParams,
        body: SetApprovalRequestSchema,
        responses: {
            200: PaymentSchema,
            400: MessageSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'Set the approval status (accounting.approve). Approved stamps the caller and the date — and the caller must not be the creator (Q-FIN-04-2); Pending/Rejected clear both (BR-FIN-04-03/-04). Inert once locked (BR-FIN-04-07). Every action is audited (Q-FIN-04-3).',
    },
    remove: {
        method: 'DELETE',
        path: '/api/payments/:paymentId',
        pathParams: paymentParams,
        body: c.noBody(),
        responses: {
            204: c.noBody(),
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'Delete a DRAFT (accounting.delete) — R8/BR-FIN-04-16, whatever the approval status; posted/cancelled answer 409.',
    },
    post: {
        method: 'POST',
        path: '/api/payments/:paymentId/post',
        pathParams: paymentParams,
        body: z.object({}).strict().optional(),
        responses: {
            200: PaymentSchema,
            400: MessageSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'Post payment (accounting.approve) — Draft → Posted: the approval gate (BR-FIN-04-01/-02) then FIN-04 §12 in ONE transaction (supplier: DR 2111 Trade Payables / CR cash; expense: DR the chosen expense account / CR cash — never Trade Payables, BR-FIN-04-09). The excess stays as supplier credit (BR-FIN-04-08). BR-FIN-04-14: a Check-method voucher whose chequeDate is strictly later credits 2116 Cheques Payable instead of cash, and the bank leg is raised on the cheque date by the cheque register (D31) — refused if the register cannot carry the cheque.',
    },
    cancel: {
        method: 'POST',
        path: '/api/payments/:paymentId/cancel',
        pathParams: paymentParams,
        /** GEN-59: never empty — the reason is the act's price of admission. */
        body: z
            .object({
            reason: z.string().trim().min(1, PAYMENT_MESSAGES.cancelReasonRequired).max(500),
        })
            .strict(),
        responses: {
            200: PaymentSchema,
            400: MessageSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'Void a DRAFT (accounting.edit) — Draft → Cancelled, the only transition beside posting; a posted voucher answers 409 (BR-FIN-04-11/-16). GEN-59: reason required, stored on the voucher and written to the audit row.',
    },
    reverse: {
        method: 'POST',
        path: '/api/payments/:paymentId/reverse',
        pathParams: paymentParams,
        body: ReversePaymentRequestSchema,
        responses: {
            200: PaymentSchema,
            400: MessageSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: "Reverse a POSTED payment (accounting.approve — reversing is a posting act, gated like Post): flips the voucher's own entry line by line (BR-FIN-02-09, exchange difference included), stamps reversed_at — which reopens every bill it paid by the amount applied (BR-FIN-04-11) — and records the required reason (audited, payment.reverse). Dated the company's today (GEN-73). One reversal ever (FIN-02 §15); refused while a reconciled bank line (BR-FIN-04-12) or a cheque record rides the voucher.",
    },
}, { strictStatusCodes: true });
//# sourceMappingURL=payments.js.map